/**
 * Chart theme adapter: pulls a curated palette + grid/axis colors from
 * sdk-utils' design tokens so every chart matches the surrounding UI
 * without manual color wiring.
 */

import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { palette } from '@repletoryreach/sdk-utils/color';

/**
 * Default series-color cycle. Picks shades from the Tailwind palette that
 * read well on both light and dark backgrounds. Override per-chart with
 * `series={[{ name, color }]}`.
 */
export function getDefaultSeriesColors(): string[] {
  return [
    palette.teal[500],
    palette.violet[500],
    palette.amber[500],
    palette.cyan[500],
    palette.pink[500],
    palette.emerald[500],
    palette.sky[500],
    palette.rose[500],
    palette.indigo[500],
    palette.lime[500]
  ];
}

export interface ChartTheme {
  axis: string;
  axisLabel: string;
  grid: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  cursor: string;
  /** Active brand color from useAppStore.primary. */
  primary: string;
  secondary: string;
  tertiary: string;
}

/** Read current theme tokens for chart rendering. */
export function useChartTheme(): ChartTheme {
  const isDark = useAppStore((s) => s.themeMode === 'dark');
  const primary = useAppStore((s) => s.primary);
  const secondary = useAppStore((s) => s.secondary);
  const tertiary = useAppStore((s) => s.tertiary);
  return {
    axis: isDark ? palette.slate[500] : palette.stone[500],
    axisLabel: isDark ? palette.slate[400] : palette.stone[600],
    grid: isDark ? palette.slate[700] : palette.stone[200],
    tooltipBg: isDark ? palette.slate[800] : '#ffffff',
    tooltipBorder: isDark ? palette.slate[700] : palette.stone[200],
    tooltipText: isDark ? palette.slate[100] : palette.stone[900],
    cursor: isDark ? palette.slate[600] : palette.stone[300],
    primary, secondary, tertiary
  };
}

export interface SeriesSpec {
  /** Property name in `data` rows. */
  name: string;
  /** Series color (hex or token). Defaults to next-in-cycle. */
  color?: string;
  /** Display name in legend / tooltip. */
  label?: string;
  /** Per-series chart type override (CompositeChart only). */
  type?: 'line' | 'bar' | 'area';
}

export function resolveSeriesColors(series: SeriesSpec[], theme: ChartTheme): SeriesSpec[] {
  const defaults = [theme.primary, theme.secondary, theme.tertiary, ...getDefaultSeriesColors()];
  return series.map((s, i) => ({ ...s, color: s.color ?? defaults[i % defaults.length] }));
}
